// Shell-level radio engine — singleton.
//
// Source-of-truth: portal-standalone.html L13740-13895 (`kosRadioWidgetInit`).
// The source instantiates a global object on `window` (`window.kosRadio`) so
// other parts of the page can toggle play/pause. In our modular world that
// global is replaced with an exported singleton.
//
// Why this lives in the shell (not in the dashboard page):
//   The dashboard widget is destroyed and recreated every time the user
//   navigates between pages. If the <audio> element lives in the widget,
//   playback stops the moment the user clicks "CRM" or "Messages". By
//   hoisting the audio element + state to the shell, the radio keeps
//   playing as the user moves around the portal.
//
// localStorage key (kos.radio.widget.v1) matches the source so a retailer
// who switches from stage to dev still has their last-chosen station + volume.
// Schema:
//   { presetId: string | null,
//     custom:   { name, streams } | null,   // only when a search result is active
//     volume:   number 0-100,
//     playing:  boolean }                    // new: auto-resume across reloads
//
// Public API (consumed by header mini-player + dashboard widget):
//   getState()                 → snapshot
//   subscribe(cb)              → unsubscribe fn
//   getPresets()               → readonly preset list
//   selectPreset(id, autoplay) → switch to a curated preset
//   selectStation(s, autoplay) → switch to a search-result station
//   play() / pause() / toggle()
//   setVolume(n)               → 0-100
//   searchStations(q)          → Promise<StationResult[]>

const LS_KEY = "kos.radio.widget.v1";

export interface RadioPreset {
  readonly id: string;
  readonly name: string;
  readonly genre: string;
  readonly bg: string;
  readonly logo: string;
  readonly streams: readonly string[];
}

export interface RadioStation {
  readonly name: string;
  readonly streams: readonly string[];
  readonly country?: string;
}

export type RadioStatus = "idle" | "loading" | "playing" | "paused" | "error";

export interface RadioState {
  readonly current: RadioStation | null;
  readonly presetId: string | null;
  readonly status: RadioStatus;
  readonly volume: number;
}

// Curated UK presets verbatim from source L13746-13764. Stream URLs are best-
// effort: each has 2–3 fallback URLs walked on error. Verified Apr 2026.
const PRESETS: readonly RadioPreset[] = [
  {
    id: "smooth",
    name: "Smooth Radio",
    genre: "Easy",
    bg: "#f97316",
    logo: "SR",
    streams: [
      "https://ice-the.musicradio.com/SmoothUKMP3",
      "http://media-ice.musicradio.com/SmoothUKMP3",
      "http://icecast.thisisglobal.com/SmoothUKMP3",
    ],
  },
  {
    id: "heart",
    name: "Heart",
    genre: "Pop",
    bg: "#e11d48",
    logo: "♥",
    streams: [
      "https://ice-the.musicradio.com/HeartUKMP3",
      "http://media-ice.musicradio.com/HeartUKMP3",
      "http://icecast.thisisglobal.com/HeartUKMP3",
    ],
  },
  {
    id: "capital",
    name: "Capital",
    genre: "Hits",
    bg: "#dc2626",
    logo: "C",
    streams: [
      "https://ice-the.musicradio.com/CapitalUKMP3",
      "http://media-ice.musicradio.com/CapitalUKMP3",
      "http://icecast.thisisglobal.com/CapitalUKMP3",
    ],
  },
  {
    id: "absolute",
    name: "Absolute Radio",
    genre: "Rock",
    bg: "#1e3a8a",
    logo: "A",
    streams: [
      "https://stream.live.vc.bbcmedia.co.uk/bbc_6music",
      "http://stream.live.vc.bbcmedia.co.uk/bbc_radio_fourfm",
    ],
  },
  {
    id: "kiss",
    name: "Kiss",
    genre: "Dance",
    bg: "#a21caf",
    logo: "K",
    streams: [
      "https://stream.live.vc.bbcmedia.co.uk/bbc_radio_one",
      "http://media-ice.musicradio.com/CapitalUKMP3",
    ],
  },
  {
    id: "bbc1",
    name: "BBC Radio 1",
    genre: "New Music",
    bg: "#dc2626",
    logo: "1",
    streams: [
      "https://stream.live.vc.bbcmedia.co.uk/bbc_radio_one",
      "https://as-hls-ww-live.akamaized.net/pool_01505109/live/ww/bbc_radio_one/bbc_radio_one.isml/bbc_radio_one-audio%3d96000.norewind.m3u8",
    ],
  },
  {
    id: "classic",
    name: "Classic FM",
    genre: "Classical",
    bg: "#7c3aed",
    logo: "♪",
    streams: [
      "https://ice-the.musicradio.com/ClassicFMMP3",
      "http://media-ice.musicradio.com/ClassicFMMP3",
      "http://icecast.thisisglobal.com/ClassicFMMP3",
    ],
  },
  {
    id: "magic",
    name: "Magic Radio",
    genre: "Variety",
    bg: "#db2777",
    logo: "M",
    streams: [
      "https://stream.live.vc.bbcmedia.co.uk/bbc_radio_two",
      "http://media-ice.musicradio.com/HeartUKMP3",
    ],
  },
];

// ── Persisted shape ─────────────────────────────────────────────────────────

interface PersistedState {
  presetId: string | null;
  custom: { name: string; streams: string[] } | null;
  volume: number;
  playing: boolean;
}

function loadPersisted(): Partial<PersistedState> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as Partial<PersistedState>;
  } catch {
    // ignore
  }
  return {};
}

function savePersisted(s: PersistedState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

// ── Engine ──────────────────────────────────────────────────────────────────

interface MutableStation {
  name: string;
  streams: string[];
  country?: string;
  _idx: number;
}

type Listener = (s: RadioState) => void;

let audio: HTMLAudioElement | null = null;
let current: MutableStation | null = null;
let presetId: string | null = null;
let status: RadioStatus = "idle";
let volume = 60;
let initialised = false;
const listeners = new Set<Listener>();

function snapshot(): RadioState {
  return {
    current: current
      ? {
          name: current.name,
          streams: current.streams,
          ...(current.country !== undefined && { country: current.country }),
        }
      : null,
    presetId,
    status,
    volume,
  };
}

function emit(): void {
  const s = snapshot();
  for (const cb of listeners) {
    try {
      cb(s);
    } catch (err) {
      console.error("[radio] listener threw:", err);
    }
  }
}

function persist(): void {
  savePersisted({
    presetId,
    custom: current && !presetId ? { name: current.name, streams: current.streams.slice() } : null,
    volume,
    playing: status === "playing" || status === "loading",
  });
}

function loadAudioSource(): void {
  if (!audio || !current) return;
  current._idx = 0;
  try {
    audio.src = current.streams[0] ?? "";
    audio.load();
  } catch {
    // ignore
  }
}

function attachAudioListeners(): void {
  if (!audio) return;

  audio.addEventListener("playing", () => {
    status = "playing";
    emit();
    persist();
  });
  audio.addEventListener("waiting", () => {
    status = "loading";
    emit();
  });
  audio.addEventListener("pause", () => {
    // Only treat as paused if it wasn't a stop / error transition.
    if (status === "playing" || status === "loading") {
      status = "paused";
      emit();
      persist();
    }
  });
  audio.addEventListener("ended", () => {
    status = "paused";
    emit();
  });
  audio.addEventListener("error", () => {
    if (!current) return;
    // Walk to the next stream in the fallback chain.
    current._idx += 1;
    const next = current.streams[current._idx];
    if (next && audio) {
      try {
        audio.src = next;
        audio.load();
        const p = audio.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => {
            /* let next error handler walk */
          });
        }
      } catch {
        status = "error";
        emit();
      }
    } else {
      status = "error";
      emit();
    }
  });
}

function ensureInit(): void {
  if (initialised) return;
  initialised = true;

  audio = document.createElement("audio");
  audio.id = "tkos-shell-radio";
  audio.preload = "none";
  audio.crossOrigin = "anonymous";
  audio.setAttribute("playsinline", "");
  audio.setAttribute("webkit-playsinline", "");
  audio.style.display = "none";
  document.body.appendChild(audio);
  attachAudioListeners();

  const persisted = loadPersisted();
  if (typeof persisted.volume === "number") {
    volume = Math.max(0, Math.min(100, persisted.volume));
  }
  audio.volume = volume / 100;

  if (persisted.presetId) {
    const p = PRESETS.find((x) => x.id === persisted.presetId);
    if (p) {
      presetId = p.id;
      current = { name: p.name, streams: p.streams.slice(), _idx: 0 };
      loadAudioSource();
    }
  } else if (persisted.custom?.streams?.length) {
    current = {
      name: persisted.custom.name,
      streams: persisted.custom.streams.slice(),
      _idx: 0,
    };
    loadAudioSource();
  }

  // Auto-resume: only if the last session was actively playing AND the
  // browser allows autoplay. Most browsers block autoplay without a user
  // gesture; we attempt anyway and fall back to "paused" silently.
  if (persisted.playing && current) {
    void play();
  }

  emit();
}

// ── Public API ──────────────────────────────────────────────────────────────

export function initRadio(): void {
  ensureInit();
}

export function getRadioState(): RadioState {
  ensureInit();
  return snapshot();
}

export function getPresets(): readonly RadioPreset[] {
  return PRESETS;
}

export function subscribe(cb: Listener): () => void {
  ensureInit();
  listeners.add(cb);
  cb(snapshot());
  return () => {
    listeners.delete(cb);
  };
}

export function selectPreset(id: string, autoplay: boolean = true): void {
  ensureInit();
  const p = PRESETS.find((x) => x.id === id);
  if (!p) return;
  presetId = p.id;
  current = { name: p.name, streams: p.streams.slice(), _idx: 0 };
  status = "idle";
  loadAudioSource();
  emit();
  persist();
  if (autoplay) void play();
}

export function selectStation(station: RadioStation, autoplay: boolean = true): void {
  ensureInit();
  presetId = null;
  current = {
    name: station.name,
    streams: station.streams.slice(),
    _idx: 0,
    ...(station.country !== undefined && { country: station.country }),
  };
  status = "idle";
  loadAudioSource();
  emit();
  persist();
  if (autoplay) void play();
}

export async function play(): Promise<void> {
  ensureInit();
  if (!audio || !current) return;
  if (!audio.src) loadAudioSource();
  status = "loading";
  emit();
  try {
    await audio.play();
  } catch (err) {
    // Autoplay blocked or stream rejected — try walking the fallback chain.
    console.warn("[radio] play() rejected:", err);
    audio.dispatchEvent(new Event("error"));
  }
}

export function pause(): void {
  ensureInit();
  if (!audio) return;
  try {
    audio.pause();
  } catch {
    // ignore
  }
}

export function toggle(): void {
  ensureInit();
  if (!current) {
    const first = PRESETS[0];
    if (first) selectPreset(first.id, true);
    return;
  }
  if (status === "playing" || status === "loading") pause();
  else void play();
}

export function setVolume(v: number): void {
  ensureInit();
  volume = Math.max(0, Math.min(100, v));
  if (audio) audio.volume = volume / 100;
  emit();
  persist();
}

// ── RadioBrowser search ─────────────────────────────────────────────────────

const RB_HOSTS = [
  "https://de1.api.radio-browser.info",
  "https://nl1.api.radio-browser.info",
  "https://at1.api.radio-browser.info",
];

export interface SearchHit {
  name: string;
  url: string;
  country?: string;
  bitrate?: number;
}

export async function searchStations(query: string, limit: number = 20): Promise<SearchHit[]> {
  const q = query.trim();
  if (!q) return [];
  const path =
    `/json/stations/search?name=${encodeURIComponent(q)}` +
    `&limit=${limit}&order=clickcount&reverse=true&hidebroken=true`;
  for (const host of RB_HOSTS) {
    try {
      const r = await fetch(host + path, { headers: { "User-Agent": "TKOS-Radio/1.0" } });
      if (!r.ok) continue;
      const data: unknown = await r.json();
      if (!Array.isArray(data)) continue;
      return data
        .filter(
          (it): it is { name?: string; url_resolved?: string; url?: string; country?: string; bitrate?: number } =>
            typeof it === "object" && it !== null,
        )
        .map((it) => ({
          name: typeof it.name === "string" ? it.name : "Unknown",
          url: typeof it.url_resolved === "string" ? it.url_resolved : typeof it.url === "string" ? it.url : "",
          ...(typeof it.country === "string" && { country: it.country }),
          ...(typeof it.bitrate === "number" && { bitrate: it.bitrate }),
        }))
        .filter((s) => s.url);
    } catch {
      // try next host
    }
  }
  return [];
}
